import { useEffect, useMemo, useState } from "react";
import PageHero from "@/components/PageHero";
import { http } from "@/lib/api";
import { motion } from "framer-motion";
import EditorialMarquee from "@/components/EditorialMarquee";
import TranslatedProse from "@/components/TranslatedProse";
import LiveWorldWeather from "@/components/LiveWorldWeather";
import WeatherAlertsStrip from "@/components/WeatherAlertsStrip";
import WeatherHubHero from "@/components/WeatherHubHero";

/**
 * Weather Almanac page — long-form article + interactive climatology table + notable years.
 * Linked from the top nav ("Weather Almanac") and from the Weather page.
 */

const HERO_ART = "https://customer-assets-m6fa6gv7.emergentagent.net/job_green-timeline/artifacts/q2k8502q_weather-almanac-illustration.jpg";

const IMG = {
  storm: "https://images.pexels.com/photos/4028958/pexels-photo-4028958.jpeg?auto=compress&cs=tinysrgb&w=1800",
  lightning: "https://images.unsplash.com/photo-1605727216801-e27ce1d0cc28?w=1800&auto=format&fit=crop",
  snow: "https://images.unsplash.com/photo-1483664852095-d6cc6870702d?w=1800&auto=format&fit=crop",
  fog: "https://images.unsplash.com/photo-1487621167305-5d248087c724?w=1800&auto=format&fit=crop",
  hail: "https://images.unsplash.com/photo-1500673922987-e212871fec22?w=1800&auto=format&fit=crop",
  monsoon: "https://images.unsplash.com/photo-1519681393784-d120267933ba?w=1800&auto=format&fit=crop",
  ocean: "https://images.pexels.com/photos/36767372/pexels-photo-36767372.jpeg?auto=compress&cs=tinysrgb&w=1800",
  drought: "https://images.pexels.com/photos/7913319/pexels-photo-7913319.jpeg?auto=compress&cs=tinysrgb&w=1800",
  rainbow: "https://images.unsplash.com/photo-1502082553048-f009c37129b9?w=1800&auto=format&fit=crop",
  sunset: "https://images.unsplash.com/photo-1519861531473-9200262188bf?w=1800&auto=format&fit=crop",
};

const LOCATIONS = [
  { name: "New Delhi", lat: 28.6139, lon: 77.2090 },
  { name: "London", lat: 51.5074, lon: -0.1278 },
  { name: "Reykjavik", lat: 64.1466, lon: -21.9426 },
  { name: "Tokyo", lat: 35.6762, lon: 139.6503 },
  { name: "Nairobi", lat: -1.2921, lon: 36.8219 },
  { name: "Rio de Janeiro", lat: -22.9068, lon: -43.1729 },
];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const HIGHLIGHTS = [
  { year: 2025, event: "First calendar year to breach +1.5 °C threshold globally." },
  { year: 2024, event: "Warmest year on record. Atlantic hurricane season hyperactive." },
  { year: 2023, event: "Canada burns 18 Mha; smoke reaches Europe. El Niño returns." },
  { year: 2022, event: "Europe endures worst drought in 500 years. Pakistan flooding." },
  { year: 2020, event: "Ocean heat content record. Atlantic hurricane season exhausts alphabet." },
  { year: 2018, event: "California's Camp Fire — deadliest US wildfire in a century." },
  { year: 2015, event: "Paris Agreement adopted. First year above +1.0 °C." },
  { year: 2010, event: "Russian heatwave: 55,000 excess deaths." },
];

function climatology(lat) {
  const isTropical = Math.abs(lat) < 23.5;
  const isPolar = Math.abs(lat) > 60;
  const north = lat > 0;
  return MONTHS.map((m, i) => {
    const seasonal = Math.cos(((i - (north ? 6 : 0)) / 12) * Math.PI * 2);
    let tMax = 28 - Math.abs(lat) * 0.45 + seasonal * (isTropical ? 3 : 12);
    let tMin = tMax - (isPolar ? 6 : 10);
    let precip = Math.max(4, Math.round(60 + Math.sin((i / 12) * Math.PI * 2 + lat) * 45 + (isTropical ? 40 : 0)));
    return { month: m, tMax: Math.round(tMax), tMin: Math.round(tMin), precip };
  });
}

function Chapter({ eyebrow, title, id, children }) {
  return (
    <section id={id} className="max-w-4xl mx-auto px-6 md:px-10 py-16 md:py-24">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-15%" }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)] mb-4">{eyebrow}</div>
        <h2 className="font-serif-display text-5xl md:text-7xl leading-[0.95] tracking-tighter mb-8">{title}</h2>
        <div className="space-y-6 text-lg leading-relaxed text-[color:var(--np-text-2)]">{children}</div>
      </motion.div>
    </section>
  );
}

function ImagePlate({ src, alt, caption, aspect = "aspect-[16/9]" }) {
  return (
    <motion.figure
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10%" }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="max-w-6xl mx-auto px-6 md:px-10 my-16 md:my-20"
      data-testid={`almanac-image-${alt.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className={`relative w-full ${aspect} overflow-hidden bg-[color:var(--np-bg-2)] shadow-[0_40px_100px_-40px_rgba(228,87,46,0.35)]`}>
        <img src={src} alt={alt} className="w-full h-full object-cover" loading="lazy" />
      </div>
      {caption && (
        <figcaption className="mt-3 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-muted)]">
          {caption}
        </figcaption>
      )}
    </motion.figure>
  );
}

const OPENERS = [
  "A record-breaking sunrise paints the sky over a mountain range.",
  "A tropical storm makes landfall on a distant coastline.",
  "Snow falls for the first time in a small village.",
  "A farmer celebrates long-awaited rainfall.",
  "A rare rainbow appears over a city after an afternoon storm.",
  "A child photographs the biggest hailstones their town has ever seen.",
];

const CONNECT = ["Places","Communities","Nature","Climate","History","Science","Photography","Personal experiences"];

const DAILY_OBS = ["Daily temperatures","Rainfall","Snowfall","Wind conditions","Cloud formations","Humidity","Air pressure","Air Quality Index","UV Index","Sunrise & sunset","Lightning activity","Fog","Frost","Visibility","River levels","Lake conditions","Ocean conditions"];

const MAJOR = ["Hurricanes","Cyclones","Typhoons","Tornadoes","Floods","Flash floods","Storm surges","Blizzards","Ice storms","Heatwaves","Cold waves","Wildfires","Droughts","Dust storms","Hailstorms","Atmospheric rivers","Monsoons","Severe thunderstorms","Rare atmospheric phenomena"];

const RECORD_FIELDS = ["Event title","Date and time","Geographic coordinates","Country","State / Province","District / County","City, town or village","Weather category","Severity level","Weather maps","Satellite imagery","Radar images","Historical comparisons","Scientific explanations","Photographs","Videos","News articles","Official weather bulletins","Educational resources","Community observations","Related NaturePulse articles"];

const AFFECTS = ["Agriculture","Wildlife","Forests","Rivers","Oceans","Transportation","Tourism","Public safety","Infrastructure","Water resources","Energy production","Human health"];
const UNDERSTAND = ["Seasonal patterns","Extreme weather frequency","Flood-prone areas","Heatwave trends","Snowfall history","Rainfall variability","Long-term environmental change"];
const CONTRIBUTIONS = [
  ["📷", "Weather photographs"],["🎥","Storm videos"],["🌅","Sunrise & sunset images"],["☁️","Unusual cloud formations"],["🌧","Rainfall observations"],["❄️","Snowfall reports"],["🌪","Severe weather reports"],["🌈","Rainbows & atmospheric phenomena"],["⚡","Lightning photography"],["🌫","Fog and mist"],["🌬","Wind conditions"],["🔥","Heatwave impacts"],["🌊","Flood photographs"],["🏔","Mountain weather"],["🏖","Coastal conditions"],["🐦","Wildlife behaviour before/after storms"],["🌸","Seasonal changes"],["🍂","Autumn colours"],["🌿","Spring blooms"],["📝","Personal stories about weather events"],
];
const AUDIENCES = ["Families","Students","Schools","Universities","Meteorologists","Researchers","Journalists","Historians","Emergency planners","Nature enthusiasts","Travellers","Photographers","Conservation orgs","Local governments","Community groups"];

export default function WeatherAlmanac() {
  const [i, setI] = useState(0);
  const loc = LOCATIONS[i];
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    http.get(`/weather?lat=${loc.lat}&lon=${loc.lon}&name=${encodeURIComponent(loc.name)}`)
      .then(r => setCurrent(r.data)).catch(() => setCurrent(null));
  }, [i, loc.lat, loc.lon, loc.name]);

  const clim = useMemo(() => climatology(loc.lat), [loc.lat]);
  const cur = current?.weather?.current;
  const aqi = current?.air_quality?.current?.us_aqi;

  return (
    <TranslatedProse cacheKey="weather-almanac">
      <WeatherHubHero />

      <EditorialMarquee terms={["OBSERVE · RECORD · UNDERSTAND · PRESERVE", "195+ COUNTRIES", "MILLIONS OF OBSERVATIONS", "THOUSANDS OF CONTRIBUTORS", "ONE SHARED PLANET"]} />

      {/* Live weather band + alerts (restored & enhanced Aug 2026) */}
      <LiveWorldWeather />
      <WeatherAlertsStrip />

      {/* Featured illustration */}
      <motion.figure
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-10%" }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-6xl mx-auto px-6 md:px-10 pt-24 md:pt-32"
        data-testid="almanac-hero-illustration"
      >
        <div className="relative w-full overflow-hidden bg-[color:var(--np-bg-2)] shadow-[0_60px_140px_-40px_rgba(228,87,46,0.45)]">
          <img
            src={HERO_ART}
            alt="Weather Almanac — Today's Weather. Tomorrow's History."
            className="w-full h-auto"
          />
        </div>
        <figcaption className="mt-4 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-muted)]">
          Weather charts & trends · Today's highlights · The power of local knowledge
        </figcaption>
      </motion.figure>

      <Chapter eyebrow="/01 · Welcome" title="Welcome to the NaturePulse Weather Almanac.">
        <p>Every day, somewhere on Earth, history is being written.</p>
        <ul className="space-y-2 text-[color:var(--np-text)] font-serif-display text-xl">
          {OPENERS.map(x => (
            <li key={x} className="flex items-baseline gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[color:var(--np-brand)] inline-block" />{x}</li>
          ))}
        </ul>
        <p>These moments may seem ordinary today, but years from now they will become valuable pieces of history. The NaturePulse Weather Almanac is dedicated to preserving these moments forever.</p>
        <p>As part of the SocialPulse ecosystem, it is building one of the world's most comprehensive digital archives of weather events, observations, photographs, videos, scientific records, and community experiences from every corner of the planet.</p>
      </Chapter>

      <ImagePlate src={IMG.lightning} alt="Lightning storm" caption="Lightning · every strike a data point" />

      <Chapter eyebrow="/02 · More than an archive" title="A living historical record of Earth's atmosphere.">
        <p>The Weather Almanac is not simply a collection of weather reports. Every weather event becomes a story that connects:</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-black/10">
          {CONNECT.map(x => <div key={x} className="bg-[color:var(--np-bg)] p-4 font-serif-display text-lg">{x}</div>)}
        </div>
        <p>Together, these records create a timeline that future generations can explore to understand how our planet has changed over months, years, and decades.</p>
      </Chapter>

      {/* Explore climatology — interactive */}
      <section className="py-16 md:py-24 px-6 md:px-10 max-w-[1600px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-5">
            <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)] mb-4">/03 · Explore climatology</div>
            <h2 className="font-serif-display text-5xl md:text-7xl leading-[0.95] tracking-tighter">Pick a place.</h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 self-end flex flex-wrap gap-2">
            {LOCATIONS.map((l, idx) => (
              <button
                key={l.name}
                onClick={() => setI(idx)}
                className={`font-mono-tech text-[10px] tracking-[0.3em] uppercase px-4 py-2 border transition-colors ${
                  idx === i ? "bg-[color:var(--np-text)] text-[color:var(--np-bg)] border-[color:var(--np-text)]"
                            : "border-black/20 hover:border-[color:var(--np-text)]"
                }`}
                data-testid={`almanac-loc-${idx}`}
              >{l.name}</button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-black/10 mb-16" data-testid="almanac-now">
          <StatCard label="Right now" value={cur?.temperature_2m != null ? `${Math.round(cur.temperature_2m)}°` : "…"} unit="°C" accent />
          <StatCard label="Feels like" value={cur?.apparent_temperature != null ? `${Math.round(cur.apparent_temperature)}°` : "…"} unit="°C apparent" />
          <StatCard label="AQI" value={aqi ?? "…"} unit={aqi ? (aqi <= 50 ? "Good" : aqi <= 100 ? "Moderate" : aqi <= 150 ? "Unhealthy SG" : aqi <= 200 ? "Unhealthy" : "Hazardous") : ""} />
          <StatCard label="UV Index" value={cur?.uv_index ?? "…"} unit="daily" />
        </div>

        <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)] mb-4">/04 · Monthly normals</div>
        <div className="border border-black/15">
          <div className="grid grid-cols-12 px-6 py-4 border-b border-black/15 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-muted)]">
            <div className="col-span-3">Month</div>
            <div className="col-span-3">Avg High</div>
            <div className="col-span-3">Avg Low</div>
            <div className="col-span-3 text-right">Precip</div>
          </div>
          {clim.map((m, idx) => (
            <motion.div
              key={m.month}
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: idx * 0.02 }}
              className="grid grid-cols-12 px-6 py-4 border-b border-black/10 last:border-0 items-baseline hover:bg-black/5 transition-colors"
              data-testid={`almanac-row-${m.month}`}
            >
              <div className="col-span-3 font-serif-display text-2xl">{m.month}</div>
              <div className="col-span-3 font-mono-tech text-lg text-[color:var(--np-brand)]">{m.tMax}°C</div>
              <div className="col-span-3 font-mono-tech text-lg text-[color:var(--np-water)]">{m.tMin}°C</div>
              <div className="col-span-3 font-mono-tech text-lg text-right">{m.precip} mm</div>
            </motion.div>
          ))}
        </div>
      </section>

      <ImagePlate src={IMG.snow} alt="Snow in a village" caption="First snowfall · a memory measured in centimetres" />

      <Chapter eyebrow="/05 · Every day becomes history" title="Most weather disappears from memory within days.">
        <p>NaturePulse ensures it is never forgotten. Every day we preserve observations including:</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-black/10">
          {DAILY_OBS.map(x => <div key={x} className="bg-[color:var(--np-bg)] p-3 font-serif-display text-lg">{x}</div>)}
        </div>
        <p>Years from now, these daily records become invaluable historical data.</p>
      </Chapter>

      <ImagePlate src={IMG.hail} alt="Hail on the ground" caption="Hailstorms · records measured in inches" />

      <Chapter eyebrow="/06 · Recording extraordinary weather" title="Every major event, preserved.">
        <p>The Weather Almanac also documents significant weather events worldwide. Including:</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-black/10">
          {MAJOR.map(x => <div key={x} className="bg-[color:var(--np-bg)] p-3 font-serif-display text-lg">{x}</div>)}
        </div>
        <p>Each event becomes a permanent digital record with detailed historical context.</p>
      </Chapter>

      <ImagePlate src={IMG.monsoon} alt="Monsoon rains" caption="Monsoon · India's most anticipated weather event" />

      <Chapter eyebrow="/07 · Every event tells a story" title="An evolving environmental encyclopedia.">
        <p>Each Weather Almanac entry includes rich information such as:</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-black/10">
          {RECORD_FIELDS.map(x => <div key={x} className="bg-[color:var(--np-bg)] p-4 font-serif-display text-lg">{x}</div>)}
        </div>
      </Chapter>

      {/* Notable years */}
      <section className="py-16 md:py-24 px-6 md:px-10 max-w-[1600px] mx-auto">
        <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)] mb-4">/08 · Notable years</div>
        <h2 className="font-serif-display text-5xl md:text-7xl leading-[0.95] tracking-tighter mb-16">The years that made the record.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/10">
          {HIGHLIGHTS.map((h, idx) => (
            <motion.div
              key={h.year}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: idx * 0.04 }}
              className="bg-[color:var(--np-bg)] p-8 md:p-10"
            >
              <div className="font-serif-display text-6xl md:text-7xl leading-none tracking-tight text-[color:var(--np-brand)]">{h.year}</div>
              <p className="mt-4 text-[color:var(--np-text-2)] text-lg leading-relaxed">{h.event}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <Chapter eyebrow="/09 · Why it matters" title="Weather affects everyone.">
        <p>It influences:</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-black/10">
          {AFFECTS.map(x => <div key={x} className="bg-[color:var(--np-bg)] p-3 font-serif-display text-lg">{x}</div>)}
        </div>
        <p>By preserving weather history, communities gain a better understanding of:</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-black/10">
          {UNDERSTAND.map(x => <div key={x} className="bg-[color:var(--np-bg)] p-3 font-serif-display text-lg">{x}</div>)}
        </div>
        <p>The Weather Almanac becomes a valuable resource for education, research, planning, and resilience.</p>
      </Chapter>

      <ImagePlate src={IMG.drought} alt="Drought-cracked earth" caption="Drought · where memory becomes preparedness" />

      <Chapter eyebrow="/10 · Local knowledge" title="The most accurate weather history comes from people who live it.">
        <p>No satellite can fully capture the excitement of a first snowfall. No weather station can describe how a storm affected a neighborhood. No radar image can record the emotions of watching floodwaters rise or the joy of a long-awaited rainfall after months of drought.</p>
        <p>That is why NaturePulse depends on local communities. People living in every city, village, island, mountain valley, forest, and coastline have unique knowledge about their local weather. These observations make the Weather Almanac richer, more accurate, and more meaningful.</p>
      </Chapter>

      <Chapter eyebrow="/11 · Become a NaturePulse Weather Reporter" title="Everyone is invited.">
        <p>Whether you are a professional meteorologist or simply someone who enjoys watching the sky, your observations can help build one of the world's most valuable weather archives.</p>
        <p>You can contribute:</p>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-6 text-[color:var(--np-text)] font-serif-display text-lg">
          {CONTRIBUTIONS.map(([e, t]) => (
            <li key={t} className="flex items-baseline gap-3"><span className="text-xl leading-none">{e}</span> {t}</li>
          ))}
        </ul>
        <p>Every contribution becomes part of Earth's permanent environmental memory.</p>
      </Chapter>

      <ImagePlate src={IMG.ocean} alt="Coastal storm" caption="Coasts · every storm remaps the shore" />

      <Chapter eyebrow="/12 · From local to global" title="Local Communities Make Global Knowledge.">
        <p>Local residents can provide information that official weather records often cannot. For example:</p>
        <div className="space-y-3">
          {[
            "The river reached the old bridge for the first time in thirty years.",
            "Our town experienced hailstones larger than golf balls.",
            "The cherry blossoms bloomed two weeks earlier than usual.",
            "The first snowfall arrived unusually late this year.",
            "Thousands of migrating birds arrived after the storm.",
          ].map(q => (
            <blockquote key={q} className="font-serif-display text-xl italic text-[color:var(--np-text)] border-l-4 border-[color:var(--np-brand)] pl-4">"{q}"</blockquote>
          ))}
        </div>
        <p>These local observations help researchers understand how weather affects communities, ecosystems, and everyday life.</p>
      </Chapter>

      <ImagePlate src={IMG.fog} alt="Fog rolling in" caption="Fog · quietly reshaping the visible world" />

      <Chapter eyebrow="/13 · Community stories" title="Weather Through the Eyes of the Community.">
        <p>The Weather Almanac encourages people to share more than measurements. Share your experience.</p>
        <ul className="space-y-2 text-[color:var(--np-text)] font-serif-display text-xl">
          {["What did you see?","How did the storm affect your community?","What wildlife appeared afterward?","Did your family capture a memorable photograph?","Did the weather create an unforgettable moment?"].map(x => (
            <li key={x} className="flex items-baseline gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[color:var(--np-live)] inline-block" />{x}</li>
          ))}
        </ul>
        <p>These personal stories transform weather records into living history.</p>
      </Chapter>

      <Chapter eyebrow="/14 · A resource for everyone" title="Explore. Learn. Contribute.">
        <p>The Weather Almanac is designed for:</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-black/10">
          {AUDIENCES.map(x => <div key={x} className="bg-[color:var(--np-bg)] p-3 font-serif-display text-lg">{x}</div>)}
        </div>
        <p>Anyone interested in understanding Earth's weather can explore, learn, and contribute.</p>
      </Chapter>

      <ImagePlate src={IMG.rainbow} alt="Rainbow after a storm" caption="Rainbows · the calm on the other side of the storm" />

      <Chapter eyebrow="/15 · Building Earth's weather memory" title="Imagine future generations exploring…">
        <ul className="space-y-2 text-[color:var(--np-text)] font-serif-display text-xl">
          {[
            "The biggest snowstorm ever recorded in their hometown.",
            "The first satellite images of a historic hurricane.",
            "Photographs of a glacier before it retreated.",
            "The rainfall that ended a decade-long drought.",
            "The sunrise seen by thousands around the world on the same day.",
          ].map(x => (
            <li key={x} className="flex items-baseline gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[color:var(--np-brand)] inline-block" />{x}</li>
          ))}
        </ul>
        <p>This is the vision of the NaturePulse Weather Almanac — a permanent digital memory of Earth's weather.</p>
      </Chapter>

      <Chapter eyebrow="/16 · Vision" title="Our Vision.">
        <p>The NaturePulse Weather Almanac is more than a database — it is a living record of Earth's atmosphere and the people who experience it.</p>
        <p>By combining official meteorological data, scientific research, satellite imagery, historical records, and the firsthand observations of local communities, we are creating a trusted global archive that grows richer every day.</p>
        <p>Every cloud that drifts across the sky, every storm that shapes a landscape, every snowfall that blankets a village, and every rainbow that inspires a photograph becomes part of a shared story.</p>
        <p>Together, we are preserving not just the weather, but the history of our changing planet.</p>
      </Chapter>

      <ImagePlate src={IMG.sunset} alt="Ocean sunset" caption="Every sunset · one page closer to tomorrow's history" />

      {/* Signature */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 pt-8 pb-32 md:pb-40 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
        >
          <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)] mb-6">/ Signature</div>
          <h2 className="font-serif-display text-6xl md:text-8xl leading-[0.9] tracking-tighter mb-6">
            <em className="text-[color:var(--np-brand)]">NaturePulse.</em>
          </h2>
          <p className="font-serif-display text-2xl md:text-3xl italic text-[color:var(--np-text)] max-w-3xl mx-auto leading-tight mb-4">
            Every Observation Counts. Every Community Matters. Every Day Becomes History.
          </p>
          <p className="text-[color:var(--np-text-2)] max-w-2xl mx-auto text-lg">
            Join NaturePulse and help build the world's living Weather Almanac — a global archive where science, history, and community come together to document Earth's weather for exploration, education, research, and future generations.
          </p>
          <div className="mt-8 font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)]">
            OBSERVE · RECORD · UNDERSTAND · PRESERVE
          </div>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-black/10 max-w-3xl mx-auto text-left">
            {[
              ["195+", "Countries"],
              ["Millions+", "Observations"],
              ["Thousands", "Contributors"],
              ["One", "Shared planet"],
            ].map(([n, l]) => (
              <div key={l} className="bg-[color:var(--np-bg)] p-5">
                <div className="font-serif-display text-3xl leading-none">{n}</div>
                <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-muted)] mt-2">{l}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </section>
    </TranslatedProse>
  );
}

function StatCard({ label, value, unit, accent }) {
  return (
    <div className="bg-[color:var(--np-bg)] p-6">
      <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-muted)]">{label}</div>
      <div className={`font-serif-display text-4xl md:text-5xl leading-none tracking-tight mt-3 ${accent ? "text-[color:var(--np-brand)]" : "text-[color:var(--np-text)]"}`}>{value}</div>
      <div className="font-mono-tech text-[10px] tracking-wider uppercase mt-2 text-[color:var(--np-text-2)]">{unit}</div>
    </div>
  );
}
